import { EcoaButton } from "@components/common/EcoaButton";
import { EcoaPopup, EcoaPopUpTextSpecForRender } from "@components/common/EcoaPopUp";
import { getLaStringValue, isEcoaImgUrlSpec } from "@lib/functions";
import { sessionSlice } from "@maven-rest/states";
import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { LaString } from "maven-lib/dist/consts";
import { useDispatch } from "react-redux";
import { Footer } from "@layouts/Footer";
import { useLangContext } from "@layouts/home/LangContext";
import { fontSizes } from "@styles/fontStyle";
import { EcoaCrfStatus } from "maven-lib/dist/dto/entity/ecoaCrf";
import { EcoaQuestionnaire, EcoaQuestionnaireData } from "maven-lib/dist/dto/entity/ecoaVisit";
import { StudyCaptureData } from "maven-lib/dist/dto/entity/studyCapture";
import { ecoaCaptureApi } from "maven-lib/dist/states/apis/ecoaCapture";
import { HbProgressBar } from "@maven-surface/components/base/HbProgressBar";
import { HbRequestButton } from "@maven-surface/components/base/HbRequestButton";
import { useRoute } from "@maven-surface/hooks/useRoute";
import { CDMS_STUDY_STATUS_CODE } from "@maven-msg/code/cdms/studyStatus";
import { ECOA_CAPTURE_CODE } from "@maven-msg/code/ecoaCapture";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

const StyledVisitQuestionnaireFooter = styled.div`
  .ecoa-button button {
    width: 100%;
    min-width: auto;
    max-width: 100%;
    height: 50px;
    padding: 0;
    border-radius: 0;
    font-weight: bold;
    font-size: ${fontSizes["BASE"]};
    text-transform: uppercase;

    .cr-icon {
      font-size: 0.9em;
    }
  }
`;

interface VisitQuestionnaireFooterProps {
  disabled?: boolean;
  buttonType?: "CAPTURE" | "EMPTY" | "NO_CAPTURE";
  isLastQuestionnaire: boolean;
  progressBarValue?: number;
  crfStatus: EcoaCrfStatus;
  folderId: string;
  visitId: string;
  visitSeq: number;
  crfPageId: string;
  crfPageSeq: number;
  questionnaire?: EcoaQuestionnaire;
  questionnairesData: EcoaQuestionnaireData;
  questionnaireSeq: number;
  getCaptureData: () => Array<StudyCaptureData | null> | null;
  isShowPopup?: boolean;
  submitConfirmPopup?: {
    confirmButtonText: LaString;
    cancelButtonText: LaString;
    popupMessage: LaString;
    popupHeader?: LaString;
  };
}

export function VisitQuestionnaireFooter({
  buttonType,
  disabled,
  isLastQuestionnaire,
  progressBarValue,
  questionnairesData,
  questionnaireSeq,
  crfStatus,
  folderId,
  visitId,
  visitSeq,
  crfPageId,
  crfPageSeq,
  questionnaire,
  getCaptureData,
  isShowPopup,
  submitConfirmPopup,
}: VisitQuestionnaireFooterProps) {
  const { pushPath, query } = useRoute();
  const dispatch = useDispatch();
  const { fontSize, lang } = useLangContext();
  const queryParams = query.from ? { from: String(query.from) } : undefined;

  const textSize = fontSize === "LARGE" ? "4X-LARGE" : fontSize === "MEDIUM" ? "2X-LARGE" : undefined;
  const dialogTextSize = fontSize === "LARGE" ? "LARGE" : fontSize === "MEDIUM" ? "MEDIUM" : undefined;

  const isItemTypeDescription = questionnaire?.item.original.type === "DESCRIPTION";

  const popupTexts = useMemo(
    () =>
      questionnairesData.popup?.texts.reduce<Array<EcoaPopUpTextSpecForRender>>((acc, cur) => {
        if (cur.img && !isEcoaImgUrlSpec(cur.img)) {
          acc.push({ ...cur, img: cur.img ? { ...cur.img, url: "" } : undefined });
        } else {
          acc.push({ ...cur, img: cur.img ? { ...cur.img, url: cur.img.url } : undefined });
        }

        return acc;
      }, []) || [],
    [questionnairesData.popup]
  );

  const confirm = submitConfirmPopup
    ? {
        confirmMessage: getLaStringValue(submitConfirmPopup.popupMessage, lang) || "",
        confirmText: getLaStringValue(submitConfirmPopup.confirmButtonText, lang) || "",
        cancelText: getLaStringValue(submitConfirmPopup.cancelButtonText, lang) || "",
        confirmHeader:
          (submitConfirmPopup.popupHeader !== undefined && getLaStringValue(submitConfirmPopup.popupHeader, lang)) || undefined,
      }
    : undefined;

  return (
    <StyledVisitQuestionnaireFooter>
      <Footer>
        {!(questionnaireSeq === 1 && isLastQuestionnaire) && (
          <EcoaButton
            disabled={questionnaireSeq === 1}
            text={MvnMessage(COMMON_LANG.PREV)}
            fontSize={textSize}
            iconLeft="ChromeBack"
            style={{ borderRight: "1px solid rgba(255, 255, 255, 0.2)" }}
            onClick={() => {
              pushPath(`/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}/${questionnaireSeq - 1}`, queryParams);
            }}
          />
        )}
        {buttonType === "CAPTURE" && (
          <>
            {isShowPopup && questionnairesData.popup && (
              <EcoaPopup title={questionnairesData.popup.title} texts={popupTexts} size={textSize} outLinedIcon={"help_outline"} />
            )}
            {!isLastQuestionnaire && isItemTypeDescription && (
              <EcoaButton
                text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)}
                fontSize={textSize}
                iconRight="ChromeBackMirrored"
                onClick={() => {
                  pushPath(
                    `/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}/${questionnaireSeq + 1}`,
                    queryParams
                  );
                }}
              />
            )}
            {!isLastQuestionnaire && !isItemTypeDescription && (
              <HbRequestButton
                disabled={disabled}
                size={textSize}
                dialogTextSize={dialogTextSize}
                api={{
                  ...ecoaCaptureApi.captureEcoaData,
                  data: {
                    data: getCaptureData() as any,
                  },
                }}
                delegate={{
                  success: () => {
                    crfStatus = EcoaCrfStatus.Ongoing;
                    pushPath(
                      `/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}/${questionnaireSeq + 1}`,
                      queryParams
                    );
                  },
                  error: (error) => {
                    if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
                      dispatch(sessionSlice.actions.signOut());
                    }

                    return MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_CAPTURE_FAIL, { message: error }, [
                      ECOA_CAPTURE_CODE.ECOA_CAPTURE_DATA_MISSING,
                    ]);
                  },
                }}
                text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)}
                iconRight="ChromeBackMirrored"
              />
            )}
            {isLastQuestionnaire && (
              <HbRequestButton
                disabled={disabled}
                size={textSize}
                dialogTextSize={dialogTextSize}
                api={{
                  ...ecoaCaptureApi.submitEcoaCaptures(folderId, visitId, visitSeq, crfPageId, crfPageSeq),
                  data: () => ({
                    data: getCaptureData() as any,
                  }),
                }}
                delegate={{
                  success: () => {
                    crfStatus = EcoaCrfStatus.Done;
                    pushPath(`/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}/done`, queryParams);
                  },
                  error: (error) => {
                    if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
                      dispatch(sessionSlice.actions.signOut());
                    }

                    return MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_SUBMIT_FAIL, { message: error }, [
                      ECOA_CAPTURE_CODE.ECOA_CAPTURE_DATA_MISSING,
                    ]);
                  },
                }}
                confirm={confirm}
                text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_SUBMIT)}
              />
            )}
          </>
        )}
        {buttonType === "EMPTY" && <EcoaButton text="" />}
        {buttonType === "NO_CAPTURE" && (
          <>
            {isLastQuestionnaire ? (
              <EcoaButton
                disabled={disabled}
                text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_DONE)}
                fontSize={textSize}
                onClick={() => pushPath("/pat/visits")}
              />
            ) : (
              <EcoaButton
                text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)}
                disabled={disabled}
                fontSize={textSize}
                iconRight="ChromeBackMirrored"
                onClick={() => {
                  pushPath(
                    `/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}/${questionnaireSeq + 1}`,
                    queryParams
                  );
                }}
              />
            )}
          </>
        )}
        {typeof progressBarValue !== "undefined" && <HbProgressBar value={progressBarValue} />}
      </Footer>
    </StyledVisitQuestionnaireFooter>
  );
}
